import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  CardTravel as TripPassIcon,
  Public as PassportIcon,
  SmartToy as AIIcon,
  Handshake as WhiteGloveIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { usePayment } from '../contexts/PaymentContext';
import { CONSUMER_PLANS, CONSUMER_ADDONS } from '../config/plans';
import { THEME_COLORS } from '../constants/theme';
import StripeCheckout from './StripeCheckout';
import ManualPaymentVerification from './ManualPaymentVerification';
import api from '../config/api';

const TIER_ICONS = {
  trip_pass: <TripPassIcon />,
  passport: <PassportIcon />,
  ai_concierge: <AIIcon />,
  white_glove_service: <WhiteGloveIcon />,
};

const TIER_COLORS = {
  trip_pass: THEME_COLORS.primary,
  passport: THEME_COLORS.secondary,
  ai_concierge: THEME_COLORS.primary,
  white_glove_service: THEME_COLORS.secondary,
};

const isAddonTier = (tierType) => (
  tierType === 'ai_concierge' || tierType === 'white_glove_service'
);

/**
 * Checkout dialog for consumer products. Trip Pass / Passport stay available in
 * config for legacy trips; AI Concierge and White Glove are the paid add-ons
 * still sold in-app.
 */
const PaymentWall = ({
  open,
  onClose,
  tripId,
  tripTitle,
  isOwner = true,
  tierType = null,
  onSuccess = null
}) => {
  const { t } = useTranslation();
  const { createCheckoutSession, loading, error, clearError } = usePayment();
  const [selectedTier, setSelectedTier] = useState(null);
  const [stripeCheckoutOpen, setStripeCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [passStatus, setPassStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const sellingAddon = isAddonTier(tierType);

  const checkPassStatus = useCallback(async () => {
    // Addon checkouts are user-scoped, not trip-pass coverage.
    if (!open || !tripId || sellingAddon) return;

    try {
      setChecking(true);
      const response = await api.get(`/payments/trip-pass/${tripId}`);
      setPassStatus(response.data);
    } catch (err) {
      console.error('Error checking Trip Pass status:', err);
      setPassStatus(null);
    } finally {
      setChecking(false);
    }
  }, [open, tripId, sellingAddon]);

  useEffect(() => {
    checkPassStatus();
  }, [checkPassStatus]);

  const handleTierSelect = (id) => {
    setStripeCheckoutOpen(false);
    setCheckoutData(null);
    clearError();
    setSelectedTier(id);
  };

  const handleCheckout = async (id) => {
    try {
      setStripeCheckoutOpen(false);
      setCheckoutData(null);
      setSelectedTier(id);

      const addon = CONSUMER_ADDONS.find((item) => item.id === id);
      if (addon?.id === 'ai_concierge' && !tripId) {
        throw new Error(t('plans.ai_concierge.tripRequired'));
      }
      const sessionData = addon
        ? await createCheckoutSession({
            tripId: addon.id === 'ai_concierge' ? tripId : null,
            tierType: addon.id
          })
        : await createCheckoutSession(tripId, id);

      if (sessionData?.alreadyCovered) {
        await checkPassStatus();
        return;
      }

      if (sessionData && (sessionData.clientSecret || sessionData.url)) {
        setCheckoutData(sessionData);
        setStripeCheckoutOpen(true);
      } else {
        throw new Error('Failed to create valid checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setStripeCheckoutOpen(false);
      setCheckoutData(null);
    }
  };

  const handleClose = () => {
    setSelectedTier(null);
    clearError();
    onClose();
  };

  const handleStripeCheckoutClose = () => {
    setStripeCheckoutOpen(false);
    setCheckoutData(null);
    setSelectedTier(null);
    clearError();
  };

  const handlePaymentSuccess = () => {
    handleStripeCheckoutClose();
    handleClose();

    if (onSuccess) {
      onSuccess();
    } else {
      window.location.reload();
    }
  };

  const billingLabel = (plan) => {
    if (plan.id === 'ai_concierge') return t('plans.ai_concierge.billingNote');
    if (plan.id === 'white_glove_service') return t('plans.white_glove_service.billingNote');
    if (plan.billing === 'year') return t('pricingPage.perYear');
    return t('pricingPage.perTrip');
  };

  // A trip an agent set up is covered for the client and everyone they invite,
  // so there is nothing to sell here — say so instead of showing prices.
  if (!sellingAddon && passStatus?.covered) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t('tripPass.coveredTitle')}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success">
            <Typography variant="body2">
              {passStatus.source === 'partner'
                ? t('tripPass.coveredByPartner')
                : passStatus.source === 'passport'
                  ? t('tripPass.coveredByPassport')
                  : t('tripPass.coveredByTripPass')}
            </Typography>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // Only the creator pays, and one purchase covers the whole group.
  if (!sellingAddon && !isOwner) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t('tripPass.memberTitle')}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">{t('tripPass.memberExplainer')}</Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('tripPass.memberFreeNote')}
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const renderStripeCheckout = () => {
    if (!stripeCheckoutOpen || !checkoutData) return null;

    return (
      <StripeCheckout
        key={`${checkoutData.sessionId}-${selectedTier}`}
        open={stripeCheckoutOpen}
        onClose={handleStripeCheckoutClose}
        clientSecret={checkoutData.clientSecret}
        tripTitle={tripTitle}
        upgradeType={selectedTier}
        quantity={1}
        onSuccess={handlePaymentSuccess}
      />
    );
  };

  const purchasable = tierType
    ? [...CONSUMER_PLANS, ...CONSUMER_ADDONS].filter((plan) => plan.id === tierType)
    : CONSUMER_PLANS.filter((plan) => plan.amount != null);

  const dialogTitle = sellingAddon
    ? t(`plans.${tierType}.paywallTitle`)
    : t('tripPass.title');
  const dialogNote = sellingAddon
    ? t(`plans.${tierType}.paywallNote`)
    : t('tripPass.planningFreeNote');

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{dialogTitle}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {checking ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{dialogNote}</Typography>
              </Alert>

              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: purchasable.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))'
                  }
                }}
              >
                {purchasable.map((plan) => {
                  const isSelected = selectedTier === plan.id;
                  const color = TIER_COLORS[plan.id] || THEME_COLORS.primary;
                  const features = t(`plans.${plan.id}.features`, { returnObjects: true });

                  return (
                    <Card
                      key={plan.id}
                      onClick={() => handleTierSelect(plan.id)}
                      sx={{
                        cursor: 'pointer',
                        border: isSelected ? `2px solid ${color}` : '1px solid #e0e0e0',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': { boxShadow: 3 }
                      }}
                    >
                      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ color, mr: 1 }}>{TIER_ICONS[plan.id]}</Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t(`plans.${plan.id}.name`)}
                          </Typography>
                          {plan.bestValue && (
                            <Chip label={t('tripPass.bestValue')} size="small" color="success" sx={{ ml: 1 }} />
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color }}>
                            ${plan.amount}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            {billingLabel(plan)}
                          </Typography>
                        </Box>

                        <List dense sx={{ flexGrow: 1 }}>
                          {(Array.isArray(features) ? features : []).map((feature) => (
                            <ListItem key={feature} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon sx={{ color, fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2' }} />
                            </ListItem>
                          ))}
                        </List>

                        <Button
                          variant={isSelected ? 'contained' : 'outlined'}
                          color="primary"
                          sx={{ mt: 2 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCheckout(plan.id);
                          }}
                          disabled={loading}
                        >
                          {loading ? <CircularProgress size={20} /> : t(`plans.${plan.id}.cta`)}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>

              {!sellingAddon && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  {t('tripPass.refundNote')}
                </Typography>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {!sellingAddon && (
                <ManualPaymentVerification
                  tripId={tripId}
                  onVerificationSuccess={() => {
                    handleClose();
                    window.location.reload();
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {renderStripeCheckout()}
    </>
  );
};

export default PaymentWall;
